import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { Spinner, ErrorBox } from '../../components/Spinner';
import { useApi } from '../../hooks/useApi';
import { getOrder, updateOrder, orderCsvUrl, uploadFileUrl, listStaff, validateOrder } from '../../api/endpoints';
import { PreviewPanel } from '../ocr/components/PreviewPanel';
import type { Staff, OrderItem, Order } from '../../types/api';

/**
 * 明細の商品名がマスタ名でなくOCR名に切り替えられているか判定。
 * 一覧の「商品名（OCR名）に切替」バッジ(has_name_override)と同じロジック。
 * 新規注文は name_overridden フラグ、旧注文は
 * 「matched_name1 == 品名+寸法 かつ matched_name2 が空」のヒューリスティックで検出。
 */
function isNameOverridden(item: OrderItem): boolean {
  if (item.name_overridden) return true;
  const matched1 = (item.matched_name1 || item.matched_name || '').trim();
  if (!matched1 || (item.matched_name2 || '').trim()) return false;
  const ocrName = [item.product_name, item.size].filter(Boolean).join(' ').trim();
  return !!ocrName && matched1 === ocrName;
}

/** 明細ごとの伝票番号(1..N)。出現順に振り直す（バックエンドの slip_groups と同じ）。 */
function slipGroupsOf(items: OrderItem[]): number[] {
  const renum = new Map<number, number>();
  let prev = 1;
  return items.map((it) => {
    if (it.slip_group == null) return prev;
    if (!renum.has(it.slip_group)) renum.set(it.slip_group, renum.size + 1);
    prev = renum.get(it.slip_group)!;
    return prev;
  });
}

/** 分割した伝票1つ分のERP状態の表示。伝票管理No.が付いていれば完了、それ以外は注文の状態に従う。 */
function slipStatus(order: Order, k: number): { no: string | null; label: string; tone: 'ok' | 'wait' | 'ng' } {
  const s = order.erp_slips?.[k - 1];
  // 突合前の古いデータ向け: 完了済みなら「A / B」を分けて使う
  const no = s?.slip_number
    ?? (order.erp_status === 'completed' ? (order.erp_slip_number || '').split(' / ')[k - 1] || null : null);
  if (no) return { no, label: '書込み完了', tone: 'ok' };
  if (s?.error) return { no: null, label: 'ERPエラー', tone: 'ng' };
  switch (order.erp_status) {
    case 'erp_written': return { no: null, label: '採番待ち', tone: 'wait' };
    case 'erp_error': return { no: null, label: '採番待ち', tone: 'wait' };
    case 'erp_processing': return { no: null, label: '書込み中', tone: 'wait' };
    case 'erp_failed': return { no: null, label: '書込み失敗', tone: 'ng' };
    case 'erp_blocked': return { no: null, label: '書込み不可', tone: 'ng' };
    default: return { no: null, label: '未送信', tone: 'wait' };
  }
}

const TONE_CLASS = {
  ok: 'bg-green-100 text-green-800',
  wait: 'bg-amber-100 text-amber-800',
  ng: 'bg-red-100 text-red-800',
} as const;

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { data, loading, error } = useApi(() => getOrder(orderId!), [orderId]);
  const [splitPercent, setSplitPercent] = useState(35);
  const [isDragging, setIsDragging] = useState(false);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [saving, setSaving] = useState(false);
  const [csvWarnings, setCsvWarnings] = useState<Array<{ field: string; message: string; level: string }>>([]);
  // 検証が落ちたのか、検証して問題が無かったのかを区別する（警告0件では判別できない）
  const [csvValidationFailed, setCsvValidationFailed] = useState(false);

  useEffect(() => {
    void (async () => {
      try { setStaffList((await listStaff()).items); } catch { /* silent */ }
    })();
  }, []);

  if (loading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const hasPreview = !!data.upload_id;
  // 見積(estimate)では先方要望で外す項目(時間指定/連絡先/受取人/届け先/相手伝票No./倉庫)を非表示にする
  const isEstimate = (data.doc_type || '').startsWith('estimate');

  function toggleSort(key: string) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  }

  const sortIndicator = (key: string) =>
    sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';

  // 伝票分割。分割していなければ slipCount=1 で、表示は従来どおり。
  const slipGroups = slipGroupsOf(data.items || []);
  const slipCount = Math.max(1, ...slipGroups);
  const isSplit = slipCount > 1;
  const slipSizes = Array.from({ length: slipCount }, (_, i) => slipGroups.filter((g) => g === i + 1).length);

  const sortedItems = (() => {
    const items = (data.items || []).map((item, i) => ({ ...item, _idx: i, _slip: slipGroups[i] }));
    if (!sortKey) return items;
    const key = sortKey as keyof typeof items[0];
    return [...items].sort((a, b) => {
      // 分割時は伝票の中だけで並べ替える。伝票をまたいで混ぜると、並び順の保存で
      // 明細とERPの行(erp_seqnos)の対応が伝票単位でずれる。
      if (a._slip !== b._slip) return a._slip - b._slip;
      // No.(line_no) は数値順、それ以外は従来通り文字列比較
      const cmp =
        sortKey === 'line_no'
          ? (Number(a.line_no) || 0) - (Number(b.line_no) || 0)
          : String(a[key] ?? '').localeCompare(String(b[key] ?? ''), 'ja');
      return sortAsc ? cmp : -cmp;
    });
  })();

  async function handleSaveSort() {
    if (!sortKey || !data) return;
    setSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const newItems = sortedItems.map(({ _idx, _slip, ...rest }) => rest);
      await updateOrder(data.order_id, { items: newItems });
      window.location.reload();
    } catch (e) {
      alert(`保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Panel className="!p-3 !mb-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">注文詳細</h2>
          {isEstimate ? (
            <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-sm font-medium">見積</span>
          ) : (
            <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-sm font-medium">注文</span>
          )}
          <span className="text-sm text-muted">{data.order_id}</span>
          <span className="text-sm px-2 py-0.5 rounded bg-blue-50 text-blue-800 font-mono">
            伝票管理No. {isSplit
              ? Array.from({ length: slipCount }, (_, i) => slipStatus(data, i + 1).no || '-').join(' / ')
              : (data.erp_slip_number || '-')}
          </span>
          {isSplit && (
            <span className="text-sm px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
              {slipCount}伝票に分割
            </span>
          )}
          <Link to="/orders">
            <Button variant="ghost">← 一覧に戻る</Button>
          </Link>
        </div>
      </Panel>

      <div className="flex" style={{ height: 'calc(100vh - 150px)' }}>
        {/* 左: プレビュー */}
        {hasPreview && (
          <>
            <div style={{ width: `${splitPercent}%` }} className="shrink-0 h-full">
              <Panel title="プレビュー" className="h-full !mb-0 flex flex-col">
                <PreviewPanel url={uploadFileUrl(data.upload_id!)} fileName={data.source_file} uploadId={data.upload_id} fileIndex={0} />
              </Panel>
            </div>
            {/* ドラッグハンドル */}
            <div
              className="w-2 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 shrink-0 rounded select-none"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDragging(true);
                const startX = e.clientX;
                const startPercent = splitPercent;
                const container = (e.target as HTMLElement).parentElement!;
                const containerWidth = container.getBoundingClientRect().width;
                const onMove = (ev: MouseEvent) => {
                  ev.preventDefault();
                  const delta = ev.clientX - startX;
                  const newPercent = startPercent + (delta / containerWidth) * 100;
                  setSplitPercent(Math.min(80, Math.max(20, newPercent)));
                };
                const onUp = () => {
                  setIsDragging(false);
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            />
            {isDragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}
          </>
        )}

        {/* 右: 詳細情報 */}
        <div className={`${hasPreview ? 'flex-1 min-w-0' : 'w-full'} h-full min-h-0`}>
          <Panel title="注文内容" className="h-full !mb-0 flex flex-col min-h-0">
            <div className="shrink-0 bg-slate-50 p-3 rounded mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <div><strong>得意先:</strong> {data.customer_name} ({data.customer_code || ''}){data.customer_abbr ? ` 略称: ${data.customer_abbr}` : ''}</div>
              <div><strong>現場:</strong> {data.site_code ? `${data.site_name || ''} (${data.site_code})` : '-'}{data.site_abbr ? ` 略称: ${data.site_abbr}` : ''}</div>
              {isEstimate && <div><strong>件名:</strong> {data.project_name || '-'}</div>}
              <div><strong>受注日付:</strong> {data.order_date || '-'}</div>
              <div><strong>注文者:</strong> {data.orderer_name || '-'}</div>
              {!isEstimate && <div><strong>時間指定:</strong> {data.time_spec || '-'}</div>}
              {!isEstimate && <div><strong>連絡先:</strong> {data.contact || '-'}</div>}
              {!isEstimate && <div><strong>受取人:</strong> {data.recipient || '-'}</div>}
              <div><strong>備考:</strong> {data.note || '-'}</div>
              <div>
                <strong>入力担当者:</strong> {(() => {
                  const raw = data.input_staff_code;
                  if (!raw) return '-';
                  return (/^\d+$/.test(raw) ? staffList.find((s) => s.code === raw)?.name : undefined) || raw;
                })()}
              </div>
              {!isEstimate && <div><strong>届け先:</strong> {data.todoke_code ? `${data.todoke_name || ''} (${data.todoke_code})`.trim() : '-'}</div>}
              {!isEstimate && <div><strong>相手伝票No.:</strong> {data.slip_no || '-'}</div>}
              <div><strong>倉庫:</strong> {data.warehouse_name || '-'}</div>
              {!hasPreview && (
                <div className="w-full text-xs text-muted">ソース: {data.source_file}</div>
              )}
            </div>

            {isSplit && (
              <div className="shrink-0 mb-3 flex gap-2 flex-wrap">
                {Array.from({ length: slipCount }, (_, i) => {
                  const st = slipStatus(data, i + 1);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => document.getElementById(`slip-${i + 1}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                      className={`text-left rounded border px-3 py-1.5 text-sm hover:bg-slate-50 ${st.tone === 'ok' ? 'border-border' : 'border-amber-400'}`}
                    >
                      <div className="font-medium">伝票 {i + 1}/{slipCount}</div>
                      <div className="font-mono text-xs text-muted">{st.no || '-'}</div>
                      <div className="text-xs">
                        <span className={`inline-block px-1.5 rounded ${TONE_CLASS[st.tone]}`}>{st.label}</span>
                        <span className="ml-1">{slipSizes[i]} 件</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="overflow-auto flex-1 min-h-0">
              <table className="whitespace-nowrap text-sm">
                <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:shadow-[inset_0_-1px_0_theme(colors.border)]">
                  <tr>
                    {isSplit && <th>伝票</th>}
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('line_no')}>No.{sortIndicator('line_no')}</th>
                    <th>行No.</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('product_name')}>品名（注文書）{sortIndicator('product_name')}</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('size')}>寸法{sortIndicator('size')}</th>
                    <th>単位</th>
                    <th className="text-right cursor-pointer select-none" onClick={() => toggleSort('quantity')}>数量{sortIndicator('quantity')}</th>
                    <th className="text-right">入数</th>
                    <th className="text-right cursor-pointer select-none" onClick={() => toggleSort('kosu')}>個数{sortIndicator('kosu')}</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('matched_name1')}>商品名1{sortIndicator('matched_name1')}</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('matched_name2')}>商品名2{sortIndicator('matched_name2')}</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('category')}>分類{sortIndicator('category')}</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('product_code')}>商品コード{sortIndicator('product_code')}</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('item_note')}>備考{isEstimate ? '1' : ''}{sortIndicator('item_note')}</th>
                    {/* 見積のみ: 明細備考2(LINCMB)・単価3列(定価/見積単価/原価)。OCR画面と列を揃える。 */}
                    {isEstimate && <th>備考2</th>}
                    {isEstimate && <th className="text-right">定価</th>}
                    {isEstimate && <th className="text-right">見積単価</th>}
                    {isEstimate && <th className="text-right">原価</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, i) => {
                    const overridden = isNameOverridden(item);
                    const nameCellClass = overridden ? 'bg-orange-50 text-orange-800' : 'text-muted';
                    const slipStarts = isSplit && (i === 0 || sortedItems[i - 1]._slip !== item._slip);
                    const st = slipStarts ? slipStatus(data, item._slip) : null;
                    return (
                    <Fragment key={i}>
                    {st && (
                      <tr id={`slip-${item._slip}`} className="bg-slate-100 border-t-2 border-slate-400">
                        <td colSpan={99} className="px-2 py-1.5 text-xs">
                          <span className="font-medium">伝票 {item._slip}/{slipCount}</span>
                          <span className="ml-3 font-mono">{st.no || '-'}</span>
                          <span className="ml-3">{slipSizes[item._slip - 1]} 件</span>
                          <span className={`ml-3 inline-block px-1.5 rounded ${TONE_CLASS[st.tone]}`}>{st.label}</span>
                          <span className="ml-3 text-muted">
                            伝票備考: {`${item._slip}/${slipCount} ${data.note || ''}`.trim()}
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr className={i % 2 === 1 ? 'bg-slate-50' : ''}>
                      {isSplit && (
                        <td className="text-center">
                          <span className="inline-block px-1.5 rounded bg-slate-100 text-xs">{item._slip}/{slipCount}</span>
                        </td>
                      )}
                      <td className="text-center">{item.line_no ?? item._idx + 1}</td>
                      <td className="text-center">
                        <span className={`inline-block px-2 py-0.5 rounded font-mono text-xs ${item.erp_line_no ? 'bg-blue-50 text-blue-800' : 'bg-slate-100 text-muted'}`}>
                          {item.erp_line_no || '-'}
                        </span>
                      </td>
                      <td>{item.product_name}</td>
                      <td>{item.size || ''}</td>
                      <td>{item.unit || ''}</td>
                      <td className="text-right">{item.quantity}</td>
                      <td className="text-right">{item.irisu || '1'}</td>
                      <td className="text-right">{item.kosu || item.quantity}</td>
                      <td className={nameCellClass}>
                        {overridden && <span className="mr-1 inline-block px-1 rounded bg-orange-200 text-orange-800 text-[10px] align-middle" title="マスタ名からOCR名に切り替えています">OCR</span>}
                        {item.matched_name1 || item.matched_name || ''}
                      </td>
                      <td className={nameCellClass}>{item.matched_name2 || ''}</td>
                      <td className="text-xs text-muted">{item.category || ''}</td>
                      <td className="text-xs font-mono text-muted">{item.product_code}</td>
                      <td className="text-xs text-muted">{item.item_note || ''}</td>
                      {/* 見積のみ: 明細備考2・単価3列 */}
                      {isEstimate && <td className="text-xs text-muted">{item.item_note2 || ''}</td>}
                      {isEstimate && <td className="text-right text-xs text-muted">{item.list_price || ''}</td>}
                      {isEstimate && <td className="text-right text-xs text-muted">{item.estimate_unit_price || ''}</td>}
                      {isEstimate && <td className="text-right text-xs text-muted">{item.cost_price || ''}</td>}
                    </tr>
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="shrink-0 mt-3 flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={async () => {
                    // 画面表示のエクスポートのため常にダウンロード可能にする。
                    // ERP検証は参考情報として警告表示のみ（ブロックしない）。
                    try {
                      const res = await validateOrder(data.order_id);
                      setCsvWarnings(res.warnings);
                      setCsvValidationFailed(false);
                    } catch (e) {
                      // 黙って捨てると「検証して問題なかった」と区別がつかない。
                      // ダウンロード自体は従来どおり止めない（画面表示のエクスポート用途）。
                      console.error('CSV出力前の検証に失敗', e);
                      setCsvWarnings([]);
                      setCsvValidationFailed(true);
                    }
                    window.location.href = orderCsvUrl(data.order_id);
                  }}
                >
                  CSV ダウンロード
                </Button>
                {sortKey && (
                  <Button onClick={() => void handleSaveSort()} disabled={saving}>
                    {saving ? '保存中...' : '並び順を保存'}
                  </Button>
                )}
              </div>
              {csvValidationFailed && (
                <div className="text-xs text-amber-600">
                  ⚠ 出力前の検証ができませんでした。CSVはダウンロードしましたが、
                  ERPに取り込めない項目が含まれている可能性があります。
                </div>
              )}
              {csvWarnings.length > 0 && (
                <div className="text-xs space-y-0.5">
                  {csvWarnings.map((w, i) => (
                    <div key={i} className={w.level === 'error' ? 'text-danger' : 'text-amber-600'}>
                      {w.level === 'error' ? '× ' : '⚠ '}{w.message}
                    </div>
                  ))}
                  {!csvWarnings.some((w) => w.level === 'error') && (
                    <div className="text-muted">桁数超過は自動的に切り詰めて出力されます</div>
                  )}
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
